import type { TMat2D } from '@/framework/shared/types/typedefs';
import { invertTransform, transformPoint } from '@/framework/shared/utils/matrix';
import { RULER_BAR_SIZE } from '../constants';

interface IRulerViewportCanvas {
    readonly viewportTransform: TMat2D;
    readonly fitPadding: number[];
    readonly size: { width: number; height: number };
    getScrollInfo(): {
        box: { width: number; height: number };
        horizontal: { needScrollbar: boolean; offset: { left: number; right: number } };
        vertical: { needScrollbar: boolean; offset: { top: number; bottom: number } };
    } | null;
    setFitPadding(padding: number | number[]): void;
    calcViewportTransform(): void;
    setViewportTransform(viewportTransform: TMat2D): void;
    refreshScrollInfo(): void;
    requestRenderAll(): void;
}

export interface IRulerViewportState {
    isPaddingApplied: boolean;
}

type TScrollPosition = 'leading' | 'middle' | 'trailing' | 'center';

function getScrollPosition(needScrollbar: boolean, leadingOffset: number, trailingOffset: number): TScrollPosition {
    if (!needScrollbar) return 'center';
    if (leadingOffset <= 0 && trailingOffset > 0) return 'leading';
    if (leadingOffset > 0 && trailingOffset <= 0) return 'trailing';
    if (leadingOffset > 0 && trailingOffset > 0) return 'middle';
    return 'center';
}

/**
 * 同步标尺占用的 fitPadding；已贴边的轴保持贴边，中间轴保持可视场景锚点。
 */
export function syncRulerViewport(edgelessCanvas: IRulerViewportCanvas, show: boolean, state: IRulerViewportState) {
    if (state.isPaddingApplied === show) return false;

    edgelessCanvas.refreshScrollInfo();
    const previousScrollInfo = edgelessCanvas.getScrollInfo();
    const horizontalPosition = previousScrollInfo
        ? getScrollPosition(
              previousScrollInfo.horizontal.needScrollbar,
              previousScrollInfo.horizontal.offset.left,
              previousScrollInfo.horizontal.offset.right,
          )
        : 'center';
    const verticalPosition = previousScrollInfo
        ? getScrollPosition(
              previousScrollInfo.vertical.needScrollbar,
              previousScrollInfo.vertical.offset.top,
              previousScrollInfo.vertical.offset.bottom,
          )
        : 'center';
    const previousOrigin = state.isPaddingApplied ? RULER_BAR_SIZE : 0;
    const previousTransform = edgelessCanvas.viewportTransform;
    const sceneAnchor = transformPoint({ x: previousOrigin, y: previousOrigin }, invertTransform(previousTransform));
    const paddingOffset = show ? RULER_BAR_SIZE : -RULER_BAR_SIZE;
    const [verticalPadding, horizontalPadding] = edgelessCanvas.fitPadding;

    edgelessCanvas.setFitPadding([verticalPadding + paddingOffset, horizontalPadding + paddingOffset]);
    state.isPaddingApplied = show;

    edgelessCanvas.calcViewportTransform();

    const nextOrigin = show ? RULER_BAR_SIZE : 0;
    const nextTransform: TMat2D = [...edgelessCanvas.viewportTransform];
    const nextZoom = nextTransform[0];
    if (horizontalPosition === 'leading') {
        nextTransform[4] = edgelessCanvas.fitPadding[1];
    } else if (horizontalPosition === 'trailing' && previousScrollInfo) {
        nextTransform[4] =
            -(previousScrollInfo.box.width * nextZoom - edgelessCanvas.size.width) - edgelessCanvas.fitPadding[1];
    } else if (horizontalPosition === 'middle') {
        nextTransform[4] = nextOrigin - sceneAnchor.x * nextZoom;
    }
    if (verticalPosition === 'leading') {
        nextTransform[5] = edgelessCanvas.fitPadding[0];
    } else if (verticalPosition === 'trailing' && previousScrollInfo) {
        nextTransform[5] =
            -(previousScrollInfo.box.height * nextZoom - edgelessCanvas.size.height) - edgelessCanvas.fitPadding[0];
    } else if (verticalPosition === 'middle') {
        nextTransform[5] = nextOrigin - sceneAnchor.y * nextZoom;
    }

    edgelessCanvas.setViewportTransform(nextTransform);
    edgelessCanvas.refreshScrollInfo();
    edgelessCanvas.requestRenderAll();
    return true;
}
